import {
    Badge,
    Button,
    Icon,
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectSeparator,
    Separator,
} from "@/shared/components";
import { SelectTrigger } from "@radix-ui/react-select";
import { Column, RowData } from "@tanstack/react-table";
import type { DataTableFeatures } from "./data-table-features";

export interface BooleanFilter {
    column: string;
    title: string;
    defaultValue?: boolean;
    "data-testid"?: string;
}

interface DataTableBooleanFilterProps<TData extends RowData, TValue> {
    column?: Column<DataTableFeatures, TData, TValue>;
    title?: string;
    defaultValue?: boolean;
    "data-testid"?: string;
}

export function DataTableBooleanFilter<TData extends RowData, TValue>({
    column,
    title,
    "data-testid": dataTestid,
}: Readonly<DataTableBooleanFilterProps<TData, TValue>>) {
    const selectedValue = column?.getFilterValue() as boolean | undefined;

    return (
        <Select
            key={`select-${selectedValue?.toString()}`} // key is needed to be able to clear the radix ui select state (reset the filter)
            value={selectedValue?.toString()}
            onValueChange={(value: string) => {
                if (value === "true") column?.setFilterValue(true);
                else if (value === "false") column?.setFilterValue(false);
                else column?.setFilterValue(undefined);
            }}
        >
            <SelectTrigger asChild>
                <Button variant="secondary" size="s" className="border-default border" data-testid={dataTestid}>
                    <Icon name="filter-alt" size="xs" />
                    {title}
                    {selectedValue !== undefined && (
                        <>
                            <Separator orientation="vertical" className="mx-2 h-4" />
                            <div className="hidden gap-1 lg:flex">
                                <Badge variant="orange-subtle" className="rounded-sm px-1 font-normal">
                                    {selectedValue === true ? "Ja" : "Nee"}
                                </Badge>
                            </div>
                        </>
                    )}
                </Button>
            </SelectTrigger>
            <SelectContent>
                <SelectGroup>
                    <SelectItem className="focus:" value="true">
                        Ja
                    </SelectItem>
                    <SelectItem className="focus:" value="false">
                        Nee
                    </SelectItem>
                </SelectGroup>

                {selectedValue !== undefined && (
                    <>
                        <SelectSeparator />
                        <div className="flex justify-center">
                            <button onClick={() => column?.setFilterValue(undefined)}>Filter wissen</button>
                        </div>
                    </>
                )}
            </SelectContent>
        </Select>
    );
}
